import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Wallet, Users, Target, TrendingUp } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace('MYR', 'RM');
};

const MetricCard = ({ icon: Icon, label, value, subtext, variant = 'default', loading }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return 'bg-success-light border-success/20';
      case 'warning':
        return 'bg-warning-light border-warning/20';
      case 'primary':
        return 'bg-primary-light border-primary/20';
      default:
        return 'bg-card border-border';
    }
  };

  const getIconStyles = () => {
    switch (variant) {
      case 'success':
        return 'bg-success/10 text-success';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'primary':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <Card className="border">
        <CardContent className="p-5">
          <Skeleton className="h-10 w-10 rounded-lg mb-3" />
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-7 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border transition-all duration-200 hover:shadow-md card-hover ${getVariantStyles()}`}>
      <CardContent className="p-5">
        <div className={`inline-flex items-center justify-center h-10 w-10 rounded-lg mb-3 ${getIconStyles()}`}>
          <Icon className="h-5 w-5" />
        </div>
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="text-xl sm:text-2xl font-display font-bold text-foreground">{value}</p>
        {subtext && (
          <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
        )}
      </CardContent>
    </Card>
  );
};

export const MetricsSection = ({ stats, loading }) => {
  const { 
    total_pledged, 
    number_of_pledges, 
    min_target, 
    remaining_allocation,
    is_min_achieved 
  } = stats;

  const metrics = [
    {
      icon: Wallet,
      label: 'Total Pledged',
      value: formatCurrency(total_pledged),
      variant: 'primary'
    },
    {
      icon: Users,
      label: 'Pledging Investors',
      value: number_of_pledges.toString(),
      subtext: 'investors committed'
    },
    {
      icon: Target,
      label: `Minimum Target (${formatCurrency(min_target)})`,
      value: is_min_achieved ? 'Achieved' : 'In Progress',
      variant: is_min_achieved ? 'success' : 'default'
    },
    {
      icon: TrendingUp,
      label: 'Remaining Allocation',
      value: formatCurrency(remaining_allocation),
      subtext: 'available to pledge',
      variant: remaining_allocation < 1000000 ? 'warning' : 'default'
    }
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4">Key Metrics</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} loading={loading} />
        ))}
      </div>
    </div>
  );
};

export default MetricsSection;
